import { css } from '@styled-system/css';
import { useEffect, useRef, useState } from 'react';

import { Logo } from './Logo';

export interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  onSelect: (option: DropdownOption) => void;
  className?: string;
  menuWidth?: string;
  maxHeight?: string;
}

export const Dropdown = ({
  options,
  value,
  placeholder = 'Sélectionner',
  onSelect,
  className,
  menuWidth = '15rem',
  maxHeight,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(
    value ? options.find((opt) => opt.value === value) || null : null,
  );
  const [menuPosition, setMenuPosition] = useState<'right' | 'left' | 'below'>('right');
  const [, forceUpdate] = useState({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Synchroniser l'option sélectionnée avec la prop value
  useEffect(() => {
    const newSelectedOption = value ? options.find((opt) => opt.value === value) || null : null;
    setSelectedOption(newSelectedOption);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculer la meilleure position pour le menu
  useEffect(() => {
    if (!isOpen || !buttonRef.current || !menuRef.current) return;

    const calculatePosition = () => {
      const trigger = buttonRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const triggerRect = trigger.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Convertir la largeur du menu en pixels
      let menuWidthPx: number;
      if (menuWidth.includes('rem')) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        menuWidthPx = parseFloat(menuWidth.replace('rem', '')) * rootFontSize;
      } else if (menuWidth.includes('px')) {
        menuWidthPx = parseFloat(menuWidth.replace('px', ''));
      } else {
        menuWidthPx = 200;
      }

      // Estimation de la hauteur du menu
      const estimatedMenuHeight = Math.min(options.length * 32 + 16, 200);

      const gap = 8;

      // Vérifier l'espace disponible
      const canFitRight = triggerRect.right + menuWidthPx + gap <= viewportWidth;
      const canFitLeft = triggerRect.left - menuWidthPx - gap >= 0;
      const canFitBelow = triggerRect.bottom + estimatedMenuHeight + gap <= viewportHeight;

      if (canFitRight) {
        setMenuPosition('right');
      } else if (canFitLeft) {
        setMenuPosition('left');
      } else if (canFitBelow) {
        setMenuPosition('below');
      } else {
        setMenuPosition('right');
      }

      // Forcer un re-render pour mettre à jour la position
      forceUpdate({});
    };

    setTimeout(calculatePosition, 0);

    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition, true);

    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, true);
    };
  }, [isOpen, options.length, menuWidth, maxHeight]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (option: DropdownOption) => {
    setSelectedOption(option);
    setIsOpen(false);
    onSelect(option);
  };

  const triggerStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'xxs', // spacing-xxs (2px)
    p: 'sm', // spacing-ms (10px) - derived from 8px + adjustments

    // Background and border from Figma
    bg: isOpen ? 'base.selectedBg' : 'base.segBg', // #222328 when open, #16171b when closed
    border: '0.5px solid',
    borderColor: 'base.border', // #1e1f24
    rounded: 'sm', // radii-ms (6px)

    // Typography from Figma
    fontSize: '2xs', // font-2xs (9px)
    fontWeight: 'semibold', // Manrope SemiBold
    fontFamily: 'Manrope, sans-serif',
    color: 'font.primary', // #e6e6e9
    textAlign: 'center',
    whiteSpace: 'nowrap',
    lineHeight: '1.07',

    // Interaction states
    cursor: 'pointer',
    transition: 'background-color 150ms ease',

    // Hover state
    _hover: {
      bg: 'base.selectedBg', // #222328
    },

    // Active state
    _active: {
      bg: 'base.selectedBg',
      opacity: 0.8,
    },

    // Remove focus outline
    _focus: {
      outline: 'none',
    },
  });

  const menuStyles = css({
    position: 'fixed',
    bg: 'base.segBg', // #16171b
    border: '0.5px solid',
    borderColor: 'base.border', // #1e1f24
    rounded: 'sm', // radii-ms (6px)
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    width: menuWidth,
    zIndex: 9999,
  });

  const getMenuPositionStyles = (): React.CSSProperties => {
    if (!buttonRef.current) return {};

    const triggerRect = buttonRef.current.getBoundingClientRect();
    const gap = 5;

    switch (menuPosition) {
      case 'right':
        return {
          top: triggerRect.top,
          left: triggerRect.right + gap,
        };
      case 'left':
        return {
          top: triggerRect.top,
          right: window.innerWidth - triggerRect.left + gap,
        };
      case 'below':
        return {
          top: triggerRect.bottom + gap,
          left: triggerRect.left,
        };
      default:
        return {
          top: triggerRect.top,
          left: triggerRect.right + gap,
        };
    }
  };

  const scrollableContainerStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'xs', // spacing-xs (5px) - gap entre les options
    px: 'xxs', // spacing-xxs (2px) - 0.5 * 4 = 2px horizontal padding
    py: 'xs', // spacing-xs (5px) vertical padding
  });

  const optionStyles = css({
    display: 'flex',
    alignItems: 'center',
    pl: 'xxs', // spacing-xxs (2px) - pl-0.5 = 2px left padding
    pr: 'md', // spacing-md (21px) - pr-[21px] right padding
    py: 'xs', // spacing-xs (5px) - py-[5px] vertical padding

    fontSize: '2xs', // font-2xs (9px)
    fontWeight: 'semibold',
    fontFamily: 'Manrope, sans-serif',
    color: 'font.primary', // #e6e6e9
    textAlign: 'left',
    whiteSpace: 'nowrap',
    lineHeight: '1.5',

    cursor: 'pointer',
    transition: 'background-color 150ms ease',
    rounded: 'xs', // radii-xs (3px)

    _hover: {
      bg: 'base.selectedBg', // #222328
    },
  });

  return (
    <div
      ref={dropdownRef}
      className={css({ position: 'relative', display: 'inline-block' }) + (className ? ` ${className}` : '')}
    >
      <button ref={buttonRef} className={triggerStyles} onClick={handleToggle} type="button">
        <span>{selectedOption?.label || placeholder}</span>
        <Logo
          variant="arrow"
          className={css({
            transform: isOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease',
          })}
        />
      </button>

      {isOpen && (
        <div ref={menuRef} className={menuStyles} style={getMenuPositionStyles()}>
          <div
            className={scrollableContainerStyles}
            style={
              maxHeight
                ? {
                    maxHeight: maxHeight,
                    overflowY: 'auto',
                  }
                : {}
            }
          >
            {options.map((option) => (
              <div
                key={option.value}
                className={optionStyles}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionSelect(option);
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
